import React, { useCallback, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import ControlMachineList from './ControlMachineList';
import { hasPermission } from '../../sc-utilities';
import { More, SettingsRemote, CloudDownload, SettingsBackupRestore } from '@material-ui/icons';
import { copyToClipBoard, createHistory } from '../../sc-utilities';
import { externalDownload } from '../../sc-utilities/download.utils';
import config from "../../../config/config";

import { snackActions } from '../../sc-utilities/SnackBar';
import * as actions from "./store/controlMachineActions";

const styles = theme => ({
    "@global": {
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: 'none',
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='active']": {
            color: theme.palette.success.dark
        },
        "[class*='inactive']": {
            color: theme.palette.error.dark
        }
    },
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    active: {
        color: theme.palette.success.dark
    },
    inactive: {
        color: theme.palette.error.dark
    },
    remote: {
        textDecoration: 'none',
        color: 'white'
    }
});

const isValid = data => {
    return data !== null && data !== undefined;
};

const ControlMachine = props => {
    const tableRef = useRef();
    const { columns, classes, translations, permissions, onRevokeControlMachine, activeCustomerID } = props;
    let visible = columns && columns['ControlMachines'];
    const [controlMachineDetailsInfo, setControlMachineDetailsInfo] = useState({ open: false });
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, ControlMachineID: null, redirectPath: '' });


    const openControlMachineDetailsModal = useCallback(async (rowData) => {
        let details = {
            ControlMachineID: rowData.ControlMachineID,
            Name: rowData.Name,
            HardwareID: rowData.HardwareID,
            RemoteID: rowData.RemoteID || translations['short_not_assigned'] || 'n/a',
            RemotePassword: rowData.RemotePassword || translations['short_not_assigned'] || 'n/a',
            License: rowData.License || null
        }
        setControlMachineDetailsInfo({ open: true, ...details });
    }, [translations]);

    const handleBackendConfigExport = async controlMachineID => {
        let objectURL = `${config.backend_domain}:${config.backend_port}/control-machine/export-backend-config?ControlMachineID=${controlMachineID}&ActiveCustomerID=${activeCustomerID}&Token=${localStorage.getItem('SESSION')}`;
        try {
            const result = await externalDownload(objectURL);
            if (!result) {
                snackActions.snackBar({
                    message: `WARNING: Cannot export backend config. Check if Command is assigned to Customer.`,
                    options: {
                        variant: 'warning'
                    }
                });
            }
        }
        catch (error) {
            snackActions.snackBar({
                message: `${error.message}`,
                options: {
                    variant: 'error'
                }
            });
        }

    }

    const handleRevoke = async (data) => {
        await onRevokeControlMachine({ HardwareID: data.HardwareID, ActiveCustomerID: activeCustomerID });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const tableColumns = useMemo(() => {
        const handleOpenAnydesk = async (event, rowData) => {
            copyToClipBoard(rowData.RemotePassword);
            snackActions.snackBarComponent((<Typography variant={'caption'} ><a className={classes.remote} rel="noopener noreferrer" href="https://anydesk.com/" target="_blank">
                {translations['securaze_remote_anydesk_snackbar_message'] || "Password is copied to clipboard. NOTE: Make sure you have installed AnyDesk"}
            </a></Typography>), 'info');
            const element = document.createElement('a');
            element.href = `anydesk:${rowData.RemoteID}`;
            element['data-href-alt'] = "https://anydesk.com/"
            return element.click();
        };

        const handleOpenRustdesk = async (event, rowData) => {
            copyToClipBoard(rowData.RemotePassword);
            snackActions.snackBarComponent((
                <Typography variant={'caption'}>
                    <a
                        className={classes.remote}
                        rel="noopener noreferrer"
                        href="https://rustdesk.com/"
                        target="_blank"
                    >
                        {translations['securaze_remote_rustdesk_snackbar_message'] || "Password is copied to clipboard. NOTE: Make sure you have installed RustDesk"}
                    </a>
                </Typography>
            ), 'info');
            const element = document.createElement('a');
            element.href = `rustdesk://${rowData.RemoteID}`;
            element['data-href-alt'] = "https://rustdesk.com/";
            return element.click();
        };

        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                filtering: false,
                render: rowData => <>
                    {
                        <Tooltip title={translations['open_remote_erasure_anydesk'] || 'Open Securaze Remote AnyDesk session'} >
                            <IconButton
                                disabled={!rowData.RemoteID || !rowData.RemotePassword || !(rowData.License?.Status === 'active' && rowData.Status === 'active')}
                                id={`control_machine_open_remote_anydesk_${rowData.ControlMachineID}`}
                                component='div'
                                onClick={() => handleOpenAnydesk(rowData)}
                            >
                                <SettingsRemote />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        <Tooltip title={translations['open_remote_erasure_rustdesk'] || 'Open Securaze Remote RustDesk session'} >
                            <IconButton
                                disabled={!rowData.RemoteID || !rowData.RemotePassword || !(rowData.License?.Status === 'active' && rowData.Status === 'active')}
                                id={`control_machine_open_remote_rustdesk_${rowData.ControlMachineID}`}
                                component='div'
                                onClick={() => handleOpenRustdesk(rowData)}
                            >
                                <SettingsRemote />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        <Tooltip title={translations['control_machine_details'] || 'Control machine details'} >
                            <IconButton
                                id={`control_machine_details_${rowData.ControlMachineID}`}
                                component='div'
                                onClick={() => openControlMachineDetailsModal(rowData)}
                            >
                                <More />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        <Tooltip title={translations['export_backend_config'] || 'Export backend config'} >
                            <IconButton
                                id={`export_backend_config_${rowData.ControlMachineID}`}
                                component='div'
                                onClick={() => handleBackendConfigExport(rowData.ControlMachineID)}
                            >
                                <CloudDownload />
                            </IconButton>
                        </Tooltip>
                    }
                    {
                        hasPermission(permissions, 'control_machine_revoke_license') && <Tooltip title={translations['revoke'] || 'Revoke'} >
                            <IconButton
                                id={`revoke_${rowData.ControlMachineID}`}
                                component='div'
                                onClick={() => handleRevoke(rowData)}
                                disabled={!rowData.ApiKey}
                            >
                                <SettingsBackupRestore />
                            </IconButton>
                        </Tooltip>
                    }
                </>
            },
            {
                title: translations["name"] || 'Name', field: 'Name',
                hidden: visible ? visible['Name'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["hardware_id"] || 'Hardware ID', field: 'HardwareID',
                hidden: visible ? visible['HardwareID'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.HardwareID ? rowData.HardwareID : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["version"] || 'Version', field: 'Version',
                hidden: visible ? visible['Version'] : false,
                filtering: false,
                render: rowData => {
                    return <Typography variant={'caption'}>{isValid(rowData.MajorVersion) && isValid(rowData.MinorVersion) && isValid(rowData.BuildNumber) ? `${rowData.MajorVersion}.${rowData.MinorVersion}.${rowData.BuildNumber}` : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["status"] || 'Status', field: 'Status',
                hidden: visible ? visible['Status'] : false,
                filtering: true,
                lookup: { 'inactive': translations["inactive"] || 'Inactive', 'active': translations["active"] || 'Active' },
                defaultFilter: ['inactive', 'active'],
                render: rowData => {
                    return <CustomChip key={`status_${rowData.ControlMachineID}`} className={rowData.License?.Status === 'active' && rowData.Status === 'active' ? 'active' : 'inactive'} value={rowData.License?.Status === 'active' && rowData.Status === 'active' ? translations["active"] || "Active" : translations["inactive"] || "Inactive"} />;
                }
            }
        ]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes.remote, openControlMachineDetailsModal, translations, visible, permissions]);

    const CustomChip = props => {
        const { keyValue, value, className } = props;
        return (
            <Typography key={keyValue} className={className}>{value}</Typography>
        );
    };


    const redirectHandler = (path, GradeID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, GradeID: GradeID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'control_machine_list') ?
                    <ControlMachineList
                        tableColumns={tableColumns}
                        controlMachineDetailsInfo={controlMachineDetailsInfo}
                        setControlMachineDetailsInfo={setControlMachineDetailsInfo}
                        tableRef={tableRef}
                        redirectHandler={redirectHandler}
                    /> : null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
}

const mapDispatchToProps = dispatch => {
    return {
        onRevokeControlMachine: (data) => dispatch(actions.revokeControlMachine(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ControlMachine));
